/**
 * The parts of the agent form that the create modal and the Intake Agents
 * page's edit modal both draw. They were one copy while both modals lived in
 * `IntakeAgent.vue`; the create modal now also opens from the Integrations page,
 * so the shared halves live here rather than being kept in step by hand.
 */
import {
  type EquitySectionCreateUser,
  createSection,
  searchPlaybook,
  setDefaultSection,
} from '@/api';
import type { GaiaRemotePage } from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import type { GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import type { SelectMixedOption } from 'naive-ui/es/select/src/interface';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

/** Both the create and the edit modal draw these same two rows. */
export const ALLOWLIST_ENTRY_TYPE_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Domain', value: 'domain' },
  { label: 'User', value: 'user' },
];

export const WEBHOOK_METHOD_OPTIONS = [
  { label: 'POST', value: 'POST' },
  { label: 'PUT', value: 'PUT' },
  { label: 'PATCH', value: 'PATCH' },
];

export const SIGNING_STATUS_OPTIONS = [
  { label: 'Signed', value: 'signed' },
  { label: 'Unsigned', value: 'unsigned' },
  { label: 'Unknown', value: 'unknown' },
];

export const CLM_SECTION_OPTIONS = [
  { label: 'Just on Insights', value: 'none' },
  { label: 'Specific CLM Sections', value: 'specific' },
  { label: 'All CLM Sections', value: 'all' },
];

export const AGENT_MODAL_TABS: GaiaTabBarItem[] = [
  { key: 'capabilities', label: 'Capabilities' },
  { key: 'permissions', label: 'Permissions' },
  { key: 'advanced', label: 'Advanced Settings' },
  { key: 'jira', label: 'Jira' },
  { key: 'slack', label: 'Slack' },
];

/**
 * Validation, in place of n-form's `:rules`. Each check names the first thing
 * wrong with its field, or null; the modal's `submitted` flag decides when the
 * message is shown, and the save handlers read the same checks so the inline
 * error and the toast can never disagree.
 */
export function capabilitiesError(capabilities: string[]): string | null {
  if (capabilities.length === 0) {
    return 'Please select at least one capability';
  }
  if (capabilities.includes('respond_with_summary') && !capabilities.includes('review')) {
    return 'Respond with Summary requires Review capability';
  }
  return null;
}

export function promptCollectionsError(
  capabilities: string[],
  collections: string[]
): string | null {
  if (capabilities.includes('review') && collections.length === 0) {
    return 'Please select at least one prompt collection when Review capability is selected';
  }
  return null;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidDomain(domain: string): boolean {
  const domainRegex = /^((?!-)[a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,}$/i;
  return domainRegex.test(domain);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/** Storage lands on the list only when the org has a GAIA token to store into. */
export function useCapabilitiesOptions(): ComputedRef<{ label: string; value: string }[]> {
  const integrationsStore = useIntegrationsStore();
  return computed(() => {
    const options = [
      { label: 'Review', value: 'review' },
      { label: 'Playbook', value: 'playbook' },
      { label: 'Respond with Summary', value: 'respond_with_summary' },
    ];

    if (integrationsStore.activeIntegrations.gaia) {
      options.push({ label: 'Import to EM', value: 'import_em' });
    }

    return options;
  });
}

export function usePromptCollectionOptions(): ComputedRef<{ label: string; value: string }[]> {
  const promptCollectionsStore = usePromptCollectionsStore();
  return computed(() =>
    promptCollectionsStore.collections.map(collection => ({
      label: collection.name,
      value: collection.uid,
    }))
  );
}

/** The CLM sections, grouped as n-select renders groups. */
export function useGaiaSectionOptions(): ComputedRef<SelectMixedOption[]> {
  const gaiaStore = useGaiaStore();
  return computed(() =>
    gaiaStore.sectionGroups.data
      .map(group => ({
        type: 'group' as const,
        label: group.title,
        key: group.id || group.title,
        children: group.sections
          .filter(section => section.id && section.title)
          .map(section => ({
            label: section.title,
            value: section.id,
          })),
      }))
      .filter(group => group.children.length > 0)
  );
}

/**
 * Playbook capability: options come from an async backend search (only active
 * playbooks). The field owns the debounce, the options, the loading flag and —
 * through `keepSelected` — the chips' labels.
 */
export async function fetchPlaybooks(text: string): Promise<GaiaRemotePage> {
  const response = await searchPlaybook({
    body: {
      text,
      limit: 50,
      column_filters: { status: ['active'] },
    },
  });
  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to search playbooks'));
  }
  return {
    options: response.data.results.map(playbook => ({
      label: playbook.name,
      value: playbook.uid,
    })),
  };
}

export function onPlaybookSearchError(error: unknown): void {
  logger.error('Error searching playbooks:', error);
}

/**
 * The CLM section groups, fetched once per modal that needs them. Only the
 * Storage capability reads them, so the fetch is deferred until that tab opens.
 */
export function useClmSections(): { isLoading: Ref<boolean>; load: () => Promise<void> } {
  const gaiaStore = useGaiaStore();
  const integrationsStore = useIntegrationsStore();
  const isLoading = ref(false);

  async function load(): Promise<void> {
    if (!integrationsStore.activeIntegrations.gaia) return;
    if (gaiaStore.sectionGroups.data.length > 0 || isLoading.value) return;

    isLoading.value = true;
    try {
      await gaiaStore.fetchSectionGroups('write');
    } catch (error) {
      logger.error('Failed to load Gaia sections:', error);
      throw new Error('Failed to load CLM sections');
    } finally {
      isLoading.value = false;
    }
  }

  return { isLoading, load };
}

export function getAllAvailableSections(): { sectionId: string; groupTitle: string | null }[] {
  const gaiaStore = useGaiaStore();
  const allSections: { sectionId: string; groupTitle: string | null }[] = [];

  for (const group of gaiaStore.sectionGroups.data) {
    for (const section of group.sections) {
      if (section.id && section.title) {
        allSections.push({
          sectionId: section.id,
          groupTitle: group.title,
        });
      }
    }
  }

  return allSections;
}

/**
 * Writes the chosen CLM sections onto a freshly created (or edited) agent.
 * Throws on failure — the caller owns the toast.
 */
export async function createEquitySections(
  agentUid: string,
  clmSections: string,
  selectedSections: string[],
  defaultSectionId?: string | null
): Promise<void> {
  const gaiaStore = useGaiaStore();
  const sanitizedDefaultSectionId =
    typeof defaultSectionId === 'string' ? defaultSectionId : undefined;
  const idToGroup = new Map<string, string>();
  const idToSection = new Map<string, string>();

  for (const group of gaiaStore.sectionGroups.data) {
    for (const sec of group.sections) {
      if (sec.id) {
        idToGroup.set(sec.id, group.title);
        idToSection.set(sec.id, sec.title ?? '');
      }
    }
  }

  let sectionsToCreate: EquitySectionCreateUser[] = [];

  if (clmSections === 'specific' && selectedSections.length) {
    sectionsToCreate = selectedSections.map(id => ({
      gaia_section_id: id,
      gaia_section_title: idToSection.get(id) ?? '',
      gaia_group_title: idToGroup.get(id) ?? null,
    }));
  } else if (clmSections === 'all') {
    sectionsToCreate = getAllAvailableSections().map(({ sectionId, groupTitle }) => ({
      gaia_section_id: sectionId,
      gaia_section_title: idToSection.get(sectionId) ?? '',
      gaia_group_title: groupTitle,
    }));
  }

  if (sectionsToCreate.length > 0) {
    await Promise.all(
      sectionsToCreate.map(async sec => {
        const response = await createSection({
          path: { intake_agent_uid: agentUid },
          body: sec,
        });

        return response;
      })
    );
  }

  // Set default section if specified
  if (typeof sanitizedDefaultSectionId === 'string' && sanitizedDefaultSectionId) {
    const sectionId = sanitizedDefaultSectionId;
    if (idToSection.has(sectionId)) {
      const defaultSection = {
        gaia_section_id: sectionId,
        gaia_section_title: idToSection.get(sectionId) ?? '',
        gaia_group_title: idToGroup.get(sectionId) ?? null,
      };
      await setDefaultSection({
        path: { intake_agent_uid: agentUid },
        body: defaultSection,
      });
    }
  }
}
